import React from 'react';
import { MapPin, DollarSign, Calendar, Star, Globe, Power, PowerOff, Edit, Trash2, FileText, Award, Briefcase } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Helper to get role acronym
const getRoleAcronym = (role) => {
  if (!role) return 'Cons';
  const roleAcronyms = {
    'Developer': 'Dev',
    'Technical Architect': 'TA',
    'Solution Architect': 'SA',
    'Business Analyst': 'BA',
    'Project Manager': 'PM',
    'Scrum Master': 'SM',
    'Technical Consultant': 'TC',
    'Functional Consultant': 'FC',
    'Implementation Specialist': 'IS',
    'Platform Owner': 'PO',
    'Admin': 'Admin',
    'System Administrator': 'SysAdmin'
  };
  // Check direct match
  if (roleAcronyms[role]) return roleAcronyms[role];
  // Check partial match
  for (const [fullRole, acronym] of Object.entries(roleAcronyms)) {
    if (role.toLowerCase().includes(fullRole.toLowerCase())) return acronym;
  }
  // Return first letters of each word
  return role.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 3);
};

// Helper to get certification acronym
const getCertAcronym = (cert) => {
  if (!cert) return '';
  // Extract acronym from parentheses like "(CIS)" or "CIS-ITSM"
  const parenthesesMatch = cert.match(/\(([A-Z-]+)\)/);
  if (parenthesesMatch) return parenthesesMatch[1];
  
  // Check for common certification patterns
  if (cert.includes('Certified Implementation Specialist')) {
    const product = cert.split('-').pop()?.trim();
    return `CIS-${product?.split(' ')[0] || ''}`.replace(/CIS-$/, 'CIS');
  }
  if (cert.includes('Certified Application Specialist')) return 'CAS';
  if (cert.includes('Certified Application Developer')) return 'CAD';
  if (cert.includes('Certified System Administrator')) return 'CSA';
  if (cert.includes('Certified Master Architect')) return 'CMA';
  if (cert.includes('Certified Technical Architect')) return 'CTA';
  if (cert.includes('Micro-Certification')) return 'μCert';
  
  // Return first 3-4 chars if nothing else works
  return cert.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 4);
};

/**
 * ResourceListView - Compact list view for resources
 */
const ResourceListView = ({ 
  resources, 
  isOwner = false,
  onToggleActivation,
  onEdit,
  onDelete,
  onViewCV,
  onNavigate,
  getAvailabilityStatus
}) => {
  if (resources.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No resources found
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-gray-50 border-b border-gray-200 text-sm font-medium text-gray-600">
        <div className="col-span-2">Name / Role</div>
        <div className="col-span-2">Location</div>
        <div className="col-span-1">Daily Rate</div>
        <div className="col-span-2">Availability</div>
        <div className="col-span-2">Products / Modules</div>
        <div className="col-span-2">Certifications</div>
        <div className="col-span-1 text-right">Actions</div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100">
        {resources.map((resource) => (
          <div 
            key={resource.id}
            className="grid grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer items-center"
            onClick={() => onNavigate && onNavigate(resource.id)}
            data-testid={`resource-row-${resource.id}`}
          >
            {/* Name / Role */}
            <div className="col-span-2">
              <div className="flex items-center gap-2">
                {isOwner && (
                  <span 
                    className={`w-2 h-2 rounded-full ${resource.is_activated ? 'bg-green-500' : 'bg-gray-300'}`}
                    title={resource.is_activated ? 'Active' : 'Inactive'}
                  />
                )}
                <div>
                  <p className="font-medium text-gray-900 truncate">
                    {resource.first_name} {resource.last_name}
                  </p>
                  <p className="text-sm text-gray-500 truncate" title={resource.role || 'Consultant'}>
                    {/* Show role acronym(s) */}
                    {resource.roles && resource.roles.length > 0 
                      ? resource.roles.slice(0, 2).map(r => getRoleAcronym(r)).join(' / ')
                      : getRoleAcronym(resource.role)}
                  </p>
                </div>
              </div>
            </div>

            {/* Location - Fixed to show city, country + Remote indicator */}
            <div className="col-span-2">
              <div className="flex flex-col text-sm text-gray-600">
                <div className="flex items-center">
                  <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                  <span className="truncate">
                    {resource.work_city || resource.location_city || resource.city || ''}{(resource.work_city || resource.location_city || resource.city) ? ', ' : ''}
                    {resource.work_country || resource.location_country || resource.country || 'N/A'}
                  </span>
                </div>
                {resource.can_work_remote && (
                  <span className="text-xs text-blue-600 flex items-center mt-0.5">
                    <Globe className="w-3 h-3 mr-1" />
                    Remote OK
                  </span>
                )}
              </div>
            </div>

            {/* Daily Rate - Fixed to always show actual rate */}
            <div className="col-span-1">
              <div className="flex items-center text-sm">
                <DollarSign className="w-3 h-3 mr-1 text-gray-400" />
                <span className="font-medium text-gray-900">
                  {resource.daily_rate || resource.pricing_min_daily || resource.pricing_max_daily 
                    ? `${resource.currency || '€'}${resource.daily_rate || resource.pricing_min_daily || resource.pricing_max_daily}`
                    : '—'}
                </span>
              </div>
            </div>

            {/* Availability */}
            <div className="col-span-2">
              {getAvailabilityStatus ? (
                getAvailabilityStatus(resource.availability_status)
              ) : (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  resource.availability_status === 'available' 
                    ? 'bg-green-100 text-green-800'
                    : resource.availability_status === 'available_soon'
                    ? 'bg-yellow-100 text-yellow-800'
                    : resource.availability_status === 'partially_available'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {resource.availability_status === 'available' ? 'Available Now' :
                   resource.availability_status === 'available_soon' ? 'Available Soon' :
                   resource.availability_status === 'partially_available' ? 'Partial' :
                   resource.availability_status === 'not_available' ? 'Not Available' :
                   resource.availability_status || 'Unknown'}
                </span>
              )}
            </div>

            {/* Products / Modules - New column */}
            <div className="col-span-2">
              <div className="flex flex-wrap gap-1">
                {/* Show products first */}
                {(resource.servicenow_products || []).slice(0, 2).map((product, idx) => (
                  <span 
                    key={`prod-${idx}`}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-50 text-purple-700"
                  >
                    {typeof product === 'string' ? product.toUpperCase() : product}
                  </span>
                ))}
                {/* Show modules if no products */}
                {(!resource.servicenow_products || resource.servicenow_products.length === 0) && 
                  (resource.modules || []).slice(0, 2).map((module, idx) => (
                    <span 
                      key={`mod-${idx}`}
                      className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-50 text-indigo-700"
                    >
                      {typeof module === 'string' ? module.split(' ')[0] : module}
                    </span>
                  ))
                }
                {((resource.servicenow_products || []).length + (resource.modules || []).length) > 2 && (
                  <span className="text-xs text-gray-400">
                    +{(resource.servicenow_products || []).length + (resource.modules || []).length - 2}
                  </span>
                )}
              </div>
            </div>

            {/* Certifications - Separate column with acronyms */}
            <div className="col-span-2">
              <div className="flex flex-wrap gap-1">
                {(resource.certifications || []).slice(0, 3).map((cert, idx) => (
                  <span 
                    key={idx}
                    className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-700"
                    title={cert}
                  >
                    {getCertAcronym(cert)}
                  </span>
                ))}
                {(resource.certifications || []).length > 3 && (
                  <span className="text-xs text-gray-400">
                    +{resource.certifications.length - 3}
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="col-span-1 flex justify-end gap-1">
              {isOwner ? (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onToggleActivation && onToggleActivation(resource.id, resource.is_activated);
                    }}
                    title={resource.is_activated ? 'Deactivate' : 'Activate'}
                  >
                    {resource.is_activated ? (
                      <Power className="w-4 h-4 text-green-600" />
                    ) : (
                      <PowerOff className="w-4 h-4 text-gray-400" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onEdit && onEdit(resource);
                    }}
                    title="Edit"
                  >
                    <Edit className="w-4 h-4 text-gray-500" />
                  </Button>
                </>
              ) : (
                resource.cv_url && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      window.open(resource.cv_url, '_blank');
                    }}
                    title="View CV"
                  >
                    <FileText className="w-4 h-4 text-gray-500" />
                  </Button>
                )
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ResourceListView;
